// lib/recastExtras.ts
// "Recast extras": the people of a photo replaced by new ones, square zone by square zone, each zone an ordinary
// qwen-image-2.1 generation. SAM finds the people on overlapping tiles and they are grouped into zones as in the owner's
// bench (zombie_bench sam_replicate.py and zones.py, validated 2026-10-08); the user corrects the zones on the canvas.

import { storageAccountName } from '#lib/azure-shared';
import { enforceStudioModelMinimumGenerations } from '#lib/model-registry';
import { QWEN_IMAGE_21_MODEL_ID } from '#lib/qwen-image-21';
import { sortLayersByRenderOrder } from '#lib/layer-render-order';
import type { KernelHandle, Layer, QwenImage21Params, Rect, RecastZone } from '#types/types';

export const RECAST_EXTRAS_RECIPE = {
  model: QWEN_IMAGE_21_MODEL_ID,
  params: {
    resolution: '2 MP',
    steps: 25,
    cfg: 3,
    cfgOneLastSteps: 5,
    noiseLevel: 0.995,
    negativePrompt: 'blurry, soft focus, out of focus, low resolution, smeared, smooth, painterly, plastic, jpeg artifacts',
  } satisfies Partial<QwenImage21Params>,
  prompt: 'Replace every person in the image by a new person of about the same age, at the same place: a new face and a new hairstyle. Each person keeps the direction they are facing. Each person looks different from the others: ordinary people with varied builds, faces and looks. No two people look alike. The number of people in the image stays exactly the same. Same environment. The final image is coherent, a realistic street photo.',
  noteLead: 'In this zone: ',
};

/** The frame is rendered for detection at its layers' native density, never above this long side. */
export const RECAST_RENDER_MAX_SIDE = 2560;
/** SAM's input: tiles are downscaled to this side before upload. */
export const RECAST_TILE_MAX_PX = 1024;
/** The route's bound on one detection; a frame needing more tiles is refused before any upload. */
export const RECAST_MAX_TILES = 64;
const TILE_FRACTION = 0.4;
const TILE_OVERLAP = 0.2;
// The bench's 150 px floor (below a 40 px tall silhouette) in the pixels of its 5334 x 3000 source photo.
const MIN_BLOB_FRAME_FRACTION = 150 / (5334 * 3000);
const MIN_BLOB_OF_LARGEST = 0.05;

/** The smallest blob kept, in a mask's pixels: the bench's floor as a share of the frame, through the tile's downscale. */
export const recastMinBlobPixels = (frameWidth: number, frameHeight: number, tileSide: number, maskWidth: number, maskHeight: number): number => (
  MIN_BLOB_FRAME_FRACTION * frameWidth * frameHeight * (maskWidth / tileSide) * (maskHeight / tileSide)
);

const TILE_ORIGIN = `https://${storageAccountName}.blob.core.windows.net`;

/** Only a tile the browser just uploaded to our own temporary storage is handed to the detector. */
export const isRecastTileUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.origin === TILE_ORIGIN && url.pathname.startsWith('/temp-public/') && !url.search;
  } catch {
    return false;
  }
};

/** [x0, y0, x1, y1], x1 and y1 exclusive. */
export type RecastBox = [number, number, number, number];

const boxArea = (b: RecastBox) => (b[2] - b[0]) * (b[3] - b[1]);

/** Where tiles of side `tile` start along `length`: every `stride`, the last flush with the far edge. */
export function recastTileStarts(length: number, tile: number, stride: number): number[] {
  const starts: number[] = [];
  for (let s = 0; s <= Math.max(length - tile, 0); s += stride) starts.push(s);
  if (starts[starts.length - 1]! + tile < length) starts.push(length - tile);
  return starts;
}

/** The square tiles covering a `width` x `height` image, row by row. */
export function recastTiles(width: number, height: number): { side: number; tiles: { x: number; y: number }[] } {
  const side = Math.min(Math.round(TILE_FRACTION * Math.max(width, height)), width, height);
  const stride = Math.floor(side * (1 - TILE_OVERLAP));
  const xs = recastTileStarts(width, side, stride);
  return { side, tiles: recastTileStarts(height, side, stride).flatMap((y) => xs.map((x) => ({ x, y }))) };
}

/** A mask's pixels on (1) or off (0), from its RGBA bytes: luminance over 127, as PIL's convert('L') then > 127. */
export function maskBits(rgba: ArrayLike<number>, width: number, height: number): Uint8Array {
  const bits = new Uint8Array(width * height);
  for (let i = 0; i < bits.length; i += 1) {
    bits[i] = 0.299 * rgba[i * 4]! + 0.587 * rgba[i * 4 + 1]! + 0.114 * rgba[i * 4 + 2]! > 127 ? 1 : 0;
  }
  return bits;
}

/**
 * One box per 8-connected blob of a mask, never the blobs' union: one detection can hold several far people. A blob is
 * kept from `minBlob` pixels (recastMinBlobPixels) and 5 % of its largest blob.
 */
export function maskBlobBoxes(bits: Uint8Array, width: number, height: number, minBlob: number): RecastBox[] {
  const seen = new Uint8Array(bits.length);
  const stack = new Int32Array(bits.length);
  const blobs: { box: RecastBox; area: number }[] = [];
  for (let start = 0; start < bits.length; start += 1) {
    if (!bits[start] || seen[start]) continue;
    let top = 0, area = 0, x0 = width, y0 = height, x1 = 0, y1 = 0;
    stack[top++] = start;
    seen[start] = 1;
    while (top > 0) {
      const p = stack[--top]!;
      const x = p % width, y = (p - x) / width;
      area += 1;
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x + 1); y1 = Math.max(y1, y + 1);
      for (let dy = -1; dy <= 1; dy += 1) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) continue;
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = x + dx;
          if (nx < 0 || nx >= width) continue;
          const q = ny * width + nx;
          if (bits[q] && !seen[q]) { seen[q] = 1; stack[top++] = q; }
        }
      }
    }
    blobs.push({ box: [x0, y0, x1, y1], area });
  }
  const largest = blobs.reduce((max, blob) => Math.max(max, blob.area), 0);
  const floor = Math.max(minBlob, MIN_BLOB_OF_LARGEST * largest);
  return blobs.filter((blob) => blob.area >= floor).map((blob) => blob.box);
}

/** Intersection over the smaller box. */
const overSmaller = (a: RecastBox, b: RecastBox) => {
  const iw = Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0]));
  const ih = Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
  return (iw * ih) / Math.max(1e-6, Math.min(boxArea(a), boxArea(b)));
};

/**
 * The people of overlapping tiles, each once: largest first, a box is dropped when it lies over 60 % inside one kept from
 * another tile (the same person seen twice), or over 90 % inside any kept one.
 */
export function mergePeopleBoxes(found: { box: RecastBox; tile: number }[]): RecastBox[] {
  const kept: { box: RecastBox; tile: number }[] = [];
  for (const b of [...found].sort((p, q) => boxArea(q.box) - boxArea(p.box))) {
    if (!kept.some((k) => overSmaller(b.box, k.box) > 0.6 && b.tile !== k.tile) && !kept.some((k) => overSmaller(b.box, k.box) > 0.9)) kept.push(b);
  }
  return kept.map((k) => k.box);
}

export const RECAST_ZONE_OPTIONS = { maxZones: 40, minDiv: 150, maxCoef: 1, link: 0.6, depthRatio: 4, margin: 0.15, hardMaxDiv: 48 };

/**
 * Square zones, each holding whole people, inside a `width` x `height` image, top to bottom then left to right. A zone is
 * the smallest square holding its people and a margin, at least width x height / minDiv, at most min(width, height) wide:
 *   1. people are grouped when the gap between their boxes is at most `link` x the smaller height and their heights are
 *      within `depthRatio` (one depth plane);
 *   2. the largest zone area is maxCoef x the largest single-person zone, never above width x height / hardMaxDiv (to which
 *      maxZones gives way) nor below the smallest zone; a group over it is split at its widest gap along its long axis;
 *   3. while there are more than maxZones, the pair adding the least area is merged;
 *   4. zones overlapping by more than 20 % of the smaller are merged while the result stays under the largest area.
 */
export function groupRecastZones(people: RecastBox[], width: number, height: number, options = RECAST_ZONE_OPTIONS): { rect: RecastBox; people: number }[] {
  if (people.length === 0) return [];
  const P = people;
  const W = width, H = height;
  const minArea = (W * H) / options.minDiv;
  const minSide = Math.sqrt(minArea);
  const h = (b: RecastBox) => b[3] - b[1];
  const gap = (p: RecastBox, q: RecastBox) => {
    const dx = Math.max(0, Math.max(p[0], q[0]) - Math.min(p[2], q[2]));
    const dy = Math.max(0, Math.max(p[1], q[1]) - Math.min(p[3], q[3]));
    return Math.sqrt(dx * dx + dy * dy);
  };

  const parent = P.map((_, i) => i);
  const find = (i: number) => {
    while (parent[i] !== i) { parent[i] = parent[parent[i]!]!; i = parent[i]!; }
    return i;
  };
  for (let i = 0; i < P.length; i += 1) {
    for (let j = i + 1; j < P.length; j += 1) {
      const hi = h(P[i]!), hj = h(P[j]!);
      if (Math.max(hi, hj) / Math.max(1, Math.min(hi, hj)) <= options.depthRatio && gap(P[i]!, P[j]!) <= options.link * Math.min(hi, hj)) {
        parent[find(i)] = find(j);
      }
    }
  }
  const groups = new Map<number, number[]>();
  P.forEach((_, i) => {
    const root = find(i);
    groups.set(root, [...(groups.get(root) ?? []), i]);
  });

  const extent = (members: number[], axis: 0 | 1) => Math.max(...members.map((i) => P[i]![axis + 2]!)) - Math.min(...members.map((i) => P[i]![axis]!));
  const rect = (members: number[]): RecastBox => {
    const heights = members.map((i) => h(P[i]!)).sort((a, b) => a - b);
    const m = options.margin * heights[Math.floor(members.length / 2)]!;
    return [
      Math.max(0, Math.min(...members.map((i) => P[i]![0])) - m), Math.max(0, Math.min(...members.map((i) => P[i]![1])) - m),
      Math.min(W, Math.max(...members.map((i) => P[i]![2])) + m), Math.min(H, Math.max(...members.map((i) => P[i]![3])) + m),
    ];
  };
  let capSide = Infinity;
  const zone = (members: number[]): RecastBox => {
    const r = rect(members);
    let side = Math.min(Math.max(r[2] - r[0], r[3] - r[1], minSide), W, H);
    // The margin gives way to the hard cap, the people themselves never do.
    if (side > capSide) side = Math.max(capSide, extent(members, 0), extent(members, 1));
    const cx = (r[0] + r[2]) / 2, cy = (r[1] + r[3]) / 2;
    const x0 = Math.min(Math.max(cx - side / 2, 0), W - side), y0 = Math.min(Math.max(cy - side / 2, 0), H - side);
    return [x0, y0, x0 + side, y0 + side];
  };

  const maxArea = Math.min((W * H) / options.hardMaxDiv, Math.max(minArea, options.maxCoef * Math.max(...P.map((_, i) => boxArea(zone([i]))))));
  capSide = Math.sqrt((W * H) / options.hardMaxDiv);

  const zones: number[][] = [];
  const todo = [...groups.values()];
  while (todo.length > 0) {
    const g = todo.pop()!;
    const r = rect(g);
    if (boxArea(zone(g)) <= maxArea || g.length === 1) {
      zones.push(g);
      continue;
    }
    const axis = r[2] - r[0] >= r[3] - r[1] ? 0 : 1;
    const order = [...g].sort((a, b) => (P[a]![axis] + P[a]![axis + 2]!) / 2 - (P[b]![axis] + P[b]![axis + 2]!) / 2);
    // The widest gap between consecutive people along the axis, edge to edge, negative when they overlap.
    let cut = 1, widest = -Infinity;
    for (let k = 1; k < order.length; k += 1) {
      const between = Math.min(...order.slice(k).map((i) => P[i]![axis]!)) - Math.max(...order.slice(0, k).map((i) => P[i]![axis + 2]!));
      if (between > widest) { widest = between; cut = k; }
    }
    todo.push(order.slice(0, cut), order.slice(cut));
  }

  while (zones.length > options.maxZones) {
    let best: { added: number; i: number; j: number } | null = null;
    for (let i = 0; i < zones.length; i += 1) {
      for (let j = i + 1; j < zones.length; j += 1) {
        const union = boxArea(zone([...zones[i]!, ...zones[j]!]));
        if (union > maxArea) continue;
        const added = union - boxArea(zone(zones[i]!)) - boxArea(zone(zones[j]!));
        if (!best || added < best.added) best = { added, i, j };
      }
    }
    if (!best) break;
    zones[best.i] = [...zones[best.i]!, ...zones[best.j]!];
    zones.splice(best.j, 1);
  }

  const intersection = (p: RecastBox, q: RecastBox) => Math.max(0, Math.min(p[2], q[2]) - Math.max(p[0], q[0])) * Math.max(0, Math.min(p[3], q[3]) - Math.max(p[1], q[1]));
  for (;;) {
    const rects = zones.map(zone);
    let best: { overlap: number; i: number; j: number } | null = null;
    for (let i = 0; i < zones.length; i += 1) {
      for (let j = i + 1; j < zones.length; j += 1) {
        const overlap = intersection(rects[i]!, rects[j]!) / Math.min(boxArea(rects[i]!), boxArea(rects[j]!));
        if (overlap > 0.2 && boxArea(zone([...zones[i]!, ...zones[j]!])) <= maxArea && (!best || overlap > best.overlap)) best = { overlap, i, j };
      }
    }
    if (!best) break;
    zones[best.i] = [...zones[best.i]!, ...zones[best.j]!];
    zones.splice(best.j, 1);
  }
  return zones.map((g) => ({ rect: zone(g), people: g.length })).sort((a, b) => a.rect[1] - b.rect[1] || a.rect[0] - b.rect[0]);
}

const EPSILON = 1e-6;
const MAX_STORED_ZONES = 200;

export const isRecastZones = (value: unknown): value is RecastZone[] => Array.isArray(value) && value.length <= MAX_STORED_ZONES
  && value.every((zone: RecastZone) => !!zone && typeof zone.id === 'string' && [zone.x, zone.y, zone.side].every(Number.isFinite) && zone.side > 0
    && typeof zone.enabled === 'boolean' && typeof zone.note === 'string');

/** Zone indexes top to bottom, then left to right: the order they run in and are numbered by. */
export const recastReadingOrder = (zones: RecastZone[]): number[] => zones
  .map((zone, i) => ({ zone, i }))
  .sort((a, b) => a.zone.y - b.zone.y || a.zone.x - b.zone.x)
  .map(({ i }) => i);

/** A zone's world rect on a workspace frame. */
const recastZoneRect = (zone: RecastZone, frame: Rect): Rect => ({ x: frame.x + zone.x, y: frame.y + zone.y, w: zone.side, h: zone.side });

/** The square kept inside the frame: no wider than its short side, moved back inside. */
export function fitRecastZone(zone: RecastZone, frame: Rect): RecastZone {
  const side = Math.min(zone.side, frame.w, frame.h);
  return { ...zone, side, x: Math.min(Math.max(zone.x, 0), frame.w - side), y: Math.min(Math.max(zone.y, 0), frame.h - side) };
}

/** The square a drag from `from` to `to` (frame-relative) draws: anchored at `from`, as large as the drag's longer side allows inside the frame. */
export function recastZoneFromDrag(from: { x: number; y: number }, to: { x: number; y: number }, frame: Rect): RecastZone {
  const dx = to.x - from.x, dy = to.y - from.y;
  const room = Math.min(dx >= 0 ? frame.w - from.x : from.x, dy >= 0 ? frame.h - from.y : from.y);
  const side = Math.max(0, Math.min(Math.max(Math.abs(dx), Math.abs(dy)), room));
  return { id: crypto.randomUUID(), x: dx >= 0 ? from.x : from.x - side, y: dy >= 0 ? from.y : from.y - side, side, enabled: true, note: '' };
}

/**
 * The pixel boxes of a detection on the frame rendered at `width` x `height` turned into zones, all enabled. Each axis has
 * its own scale, the render's dimensions being rounded, and each zone is kept square inside the frame.
 */
export const recastZonesFromPixels = (rects: RecastBox[], width: number, height: number, frame: Rect): RecastZone[] => rects.map((r) => fitRecastZone({
  id: crypto.randomUUID(), x: (r[0] * frame.w) / width, y: (r[1] * frame.h) / height, side: ((r[2] - r[0]) * frame.w) / width, enabled: true, note: '',
}, frame));

export const recastPrompt = (note: string) => (note.trim() ? `${RECAST_EXTRAS_RECIPE.prompt}\n\n${RECAST_EXTRAS_RECIPE.noteLead}${note.trim()}` : RECAST_EXTRAS_RECIPE.prompt);

/** The enabled zones in reading order, each with its number among all zones, its world rect, its own words and its prompt. */
export const recastExtrasSteps = (zones: RecastZone[], frame: Rect) => recastReadingOrder(zones)
  .map((i, position) => ({ zone: zones[i]!, number: position + 1 }))
  .filter(({ zone }) => zone.enabled)
  .map(({ zone, number }) => ({ number, rect: recastZoneRect(zone, frame), note: zone.note.trim(), prompt: recastPrompt(zone.note) }));

/**
 * What keeps the recipe from running as one generation at a time; empty when it can. One generation per zone, so that
 * its price is one quote and each zone sees the ones before it landed.
 */
export const recastRecipeErrors = (): string[] => (enforceStudioModelMinimumGenerations(RECAST_EXTRAS_RECIPE.model, 1) !== 1
  ? [`The model ${RECAST_EXTRAS_RECIPE.model} cannot run one generation alone.`] : []);

/** What keeps Recast extras from running on these zones of `frame`; empty when it can. */
export function recastExtrasErrors(zones: RecastZone[], frame: Rect): string[] {
  const errors = recastRecipeErrors();
  if (!isRecastZones(zones)) {
    errors.push('The zones must be squares, each with its note.');
    return errors;
  }
  if (!zones.some((zone) => zone.enabled)) errors.push('Select at least one zone.');
  recastReadingOrder(zones).forEach((i, position) => {
    const zone = zones[i]!;
    if (zone.enabled && (zone.x < -EPSILON || zone.y < -EPSILON || zone.x + zone.side > frame.w + EPSILON || zone.y + zone.side > frame.h + EPSILON)) {
      errors.push(`Zone ${position + 1} lies outside the frame: move it inside.`);
    }
  });
  return errors;
}

const zonesOf = (layer: Layer) => (layer.params as { recastZones?: unknown } | undefined)?.recastZones;

/** The bottom visible image layer: the photo the zones were drawn on, under every recast landed since. */
const bottomPhoto = (layers: Layer[]) => sortLayersByRenderOrder(layers).find((layer) => layer.visible && !layer.type);

/** The zones a workspace remembers, from its lowest visible layer carrying some; null when none were validated on it. */
export const rememberedRecastZones = (layers: Layer[]): RecastZone[] | null => {
  const zones = sortLayersByRenderOrder(layers).filter((layer) => layer.visible).map(zonesOf).find(isRecastZones);
  return zones ?? null;
};

/** Remembers the zones on the workspace's bottom photo, saved as any layer change, outside the undo history. */
export function rememberRecastZones(kernel: KernelHandle, workspaceId: string, zones: RecastZone[]): void {
  const photo = bottomPhoto(kernel.getLayersInWorkspaceContext(workspaceId));
  if (!photo) return;
  kernel.setLayer(photo.id, (existing) => ({ ...existing, params: { ...existing.params, recastZones: zones } }));
  void kernel.saveLayer(photo.id).catch(() => kernel.handleError('The recast zones could not be saved.'));
}
